/* Panel Registry — zone assignment, tab bars, panel switching */

import { state, refs } from './state.js';
import { collapseZone, expandZone } from './layout-manager.js';

function _isZoneCollapsed(zoneId) {
  if (zoneId === 'left')   return !state.leftZoneVisible;
  if (zoneId === 'right')  return !state.rightZoneVisible;
  if (zoneId === 'bottom') return !state.bottomZoneVisible;
  return false;
}

// ---------------------------------------------------------------------------
// Panel definitions
// ---------------------------------------------------------------------------

export const PANELS = {
  explorer:       { id: 'explorer',       label: 'Explorer',        icon: '\u{1F4C1}', defaultZone: 'left',   pinned: false },
  'exec-order':   { id: 'exec-order',     label: 'Execution Order', icon: '\u2630',     defaultZone: 'left',   pinned: false },
  knowledge:      { id: 'knowledge',      label: 'Knowledge',       icon: '\u{1F4D6}', defaultZone: 'left',   pinned: false },
  'db-inspector': { id: 'db-inspector',   label: 'DB Inspector',    icon: '\u{1F5C3}', defaultZone: 'left',   pinned: false },
  'sync-status':  { id: 'sync-status',    label: 'Sync Status',     icon: '\u21C5',     defaultZone: 'left',   pinned: false },
  // icon field unused by the activity bar (that button is hand-coded in index.html as ic-rewind)
  timeline:       { id: 'timeline',       label: 'Timeline',        icon: '\u21BA',     defaultZone: 'left',   pinned: false },
  // icon field unused by the activity bar (hand-coded in index.html as ic-activity)
  traces:         { id: 'traces',         label: 'Traces',          icon: '\u2248',     defaultZone: 'left',   pinned: false },
  // icon field unused by the activity bar (hand-coded in index.html as ic-git-pull-request)
  promotion:      { id: 'promotion',      label: 'Promotion',       icon: '\u21E7',     defaultZone: 'left',   pinned: false },
  // icon field unused by the activity bar (hand-coded in index.html as ic-alert-triangle)
  errors:         { id: 'errors',         label: 'Errors',          icon: '\u26a0',     defaultZone: 'left',   pinned: false },
  // icon field unused by the activity bar (hand-coded in index.html as ic-book-open)
  learning:       { id: 'learning',       label: 'Learning',        icon: '\u2699',     defaultZone: 'left',   pinned: false },
  editor:         { id: 'editor',         label: 'Editor',          icon: '\u270E',     defaultZone: 'center', pinned: true  },
  diff:           { id: 'diff',           label: 'Diff',            icon: '\u21C4',     defaultZone: 'center', pinned: false },
  pipelines:      { id: 'pipelines',      label: 'Pipelines',       icon: '\u21C9',     defaultZone: 'center', pinned: false },
  chat:           { id: 'chat',           label: 'Chat',            icon: '\u26A1',     defaultZone: 'right',  pinned: false, allowedZones: ['right', 'bottom'] },
  'quote-frame':  { id: 'quote-frame',    label: 'Quote Frame',     icon: '\u{1F4C4}', defaultZone: 'right',  pinned: false, allowedZones: ['right', 'bottom'] },
  console:        { id: 'console',        label: 'Console',         icon: '\u25B6',     defaultZone: 'right',  pinned: false, allowedZones: ['right', 'bottom'] },
  configurator:   { id: 'configurator',   label: 'Configurator',    icon: '\u2699',     defaultZone: 'bottom', pinned: false, allowedZones: ['right', 'bottom'] },
};

const ZONE_IDS = ['left', 'center', 'right', 'bottom'];

const DEFAULT_ZONE_MAP = {
  left:   ['explorer'],
  center: ['editor'],
  right:  ['chat', 'quote-frame', 'console'],
  bottom: [],
};

const DEFAULT_ACTIVE = {
  left:   'explorer',
  center: 'editor',
  right:  'chat',
  bottom: null,
};

const HIDDEN_BY_DEFAULT = new Set(['exec-order', 'knowledge', 'db-inspector', 'diff', 'sync-status', 'timeline', 'traces', 'promotion', 'errors', 'learning', 'pipelines']);

// ---------------------------------------------------------------------------
// Internal state
// ---------------------------------------------------------------------------

/** Set of panel IDs currently hidden (not in any zone) */
const hiddenPanels = new Set();

/** { zoneId: [panelId, ...] } — ordered list of panels per zone */
let zoneMap = {};

/** { zoneId: panelId } — which panel is active (visible) per zone */
let activePanels = {};

/** Cache of panel container elements — survives hide/show (detach/reattach) cycles */
const panelContainers = {};

// ---------------------------------------------------------------------------
// DOM helpers
// ---------------------------------------------------------------------------

function capitalize(s) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

/** Get the tab bar element for a zone */
function getTabBar(zoneId) {
  return refs[`zone${capitalize(zoneId)}Tabs`] || document.getElementById(`zone${capitalize(zoneId)}Tabs`);
}

/** Get the content area element for a zone */
function getContentArea(zoneId) {
  return refs[`zone${capitalize(zoneId)}Content`] || document.getElementById(`zone${capitalize(zoneId)}Content`);
}

/** Look up a panel container — checks live DOM first, then the detached-element cache */
function _getContainer(panelId) {
  return document.getElementById(`panel-${panelId}`) || panelContainers[panelId] || null;
}

// ---------------------------------------------------------------------------
// Tab rendering
// ---------------------------------------------------------------------------

function _renderZoneTabs(zoneId) {
  // Left zone uses activity bar, center zone uses file tabs — skip panel tabs for both
  if (zoneId === 'left' || zoneId === 'center') return;

  const tabBar = getTabBar(zoneId);
  if (!tabBar) return;

  const panels = zoneMap[zoneId] || [];
  tabBar.innerHTML = '';

  for (const panelId of panels) {
    const def = PANELS[panelId];
    if (!def) continue;

    const btn = document.createElement('button');
    btn.className = 'zone-tab';
    btn.dataset.panel = panelId;
    btn.textContent = def.label;

    if (activePanels[zoneId] === panelId) {
      btn.classList.add('active');
    }

    if (!def.pinned) {
      btn.draggable = true;

      btn.addEventListener('dragstart', (e) => {
        e.dataTransfer.setData(DRAG_MIME, panelId);
        e.dataTransfer.effectAllowed = 'move';
        btn.classList.add('dragging');
        _panelDragActive = true;
      });
      btn.addEventListener('dragend', () => {
        btn.classList.remove('dragging');
        _panelDragActive = false;
      });
    }

    btn.addEventListener('click', () => {
      _activatePanel(zoneId, panelId);
    });

    btn.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      _showZoneTabContextMenu(e, panelId);
    });

    tabBar.appendChild(btn);
  }

  _renderZoneTabMenuButton(zoneId, tabBar);
}

// ---------------------------------------------------------------------------
// Dock constraints + "+" reopen button
// ---------------------------------------------------------------------------

/** Return true if a panel can be dropped/docked into the given zone. */
function _canDropInZone(panelId, targetZone) {
  if (!panelId) return false;
  const def = PANELS[panelId];
  if (!def || def.pinned) return false;
  if (def.allowedZones) return def.allowedZones.includes(targetZone);
  // Only editor+diff+pipelines allowed in center
  if (targetZone === 'center') return panelId === 'editor' || panelId === 'diff' || panelId === 'pipelines';
  // Left-zone-default panels (explorer, exec-order, etc.) stay in left
  if (def.defaultZone === 'left' && targetZone !== 'left') return false;
  return true;
}

/** The hidden panels (allowedZones-compatible with the given zone) that can be reopened here. */
function _hiddenPanelsForZone(zoneId) {
  const out = [];
  for (const panelId of hiddenPanels) {
    const def = PANELS[panelId];
    if (!def) continue;
    if (def.allowedZones && !def.allowedZones.includes(zoneId)) continue;
    // Skip panels that are really owned by left zone (activity bar manages them)
    if (!def.allowedZones && def.defaultZone === 'left' && zoneId !== 'left') continue;
    if (!def.allowedZones && def.defaultZone === 'center') continue;
    out.push(def);
  }
  return out;
}

/** Render (or remove) a trailing "+" button at the end of a zone's tab bar. */
function _renderZoneTabMenuButton(zoneId, tabBar) {
  if (zoneId === 'left' || zoneId === 'center') return;
  const candidates = _hiddenPanelsForZone(zoneId);
  if (!candidates.length) return;

  const btn = document.createElement('button');
  btn.className = 'zone-tab-menu-btn';
  btn.type = 'button';
  btn.title = 'Reopen closed panel';
  btn.textContent = '+';
  btn.addEventListener('click', (e) => {
    e.stopPropagation();
    _showZoneTabMenuDropdown(btn, zoneId, candidates);
  });
  tabBar.appendChild(btn);
}

// ---------------------------------------------------------------------------
// Tab context menu (right-click) + "+" dropdown
// ---------------------------------------------------------------------------

let _ctxMenuEl = null;
let _ctxMenuOutsideHandler = null;
let _ctxMenuEscHandler = null;

function _closeCtxMenu() {
  if (_ctxMenuOutsideHandler) {
    document.removeEventListener('mousedown', _ctxMenuOutsideHandler, true);
    _ctxMenuOutsideHandler = null;
  }
  if (_ctxMenuEscHandler) {
    document.removeEventListener('keydown', _ctxMenuEscHandler, true);
    _ctxMenuEscHandler = null;
  }
  if (_ctxMenuEl && _ctxMenuEl.parentNode) {
    _ctxMenuEl.parentNode.removeChild(_ctxMenuEl);
  }
  _ctxMenuEl = null;
}

function _openCtxMenu(items, x, y) {
  _closeCtxMenu();
  const menu = document.createElement('div');
  menu.className = 'ws-ctx-menu zone-tab-ctx-menu';
  for (const item of items) {
    if (item.sep) {
      const sep = document.createElement('div');
      sep.className = 'ws-ctx-sep';
      menu.appendChild(sep);
      continue;
    }
    const btn = document.createElement('button');
    btn.className = 'ws-ctx-item' + (item.danger ? ' danger' : '');
    btn.type = 'button';
    btn.textContent = item.label;
    if (item.disabled) {
      btn.disabled = true;
    } else {
      btn.addEventListener('click', () => {
        _closeCtxMenu();
        item.action();
      });
    }
    menu.appendChild(btn);
  }
  document.body.appendChild(menu);

  // Position (after mount so we can measure)
  const rect = menu.getBoundingClientRect();
  const xClamped = Math.min(x, window.innerWidth - rect.width - 8);
  const yClamped = Math.min(y, window.innerHeight - rect.height - 8);
  menu.style.left = `${Math.max(8, xClamped)}px`;
  menu.style.top = `${Math.max(8, yClamped)}px`;

  _ctxMenuEl = menu;

  _ctxMenuOutsideHandler = (ev) => {
    if (!_ctxMenuEl || _ctxMenuEl.contains(ev.target)) return;
    _closeCtxMenu();
  };
  _ctxMenuEscHandler = (ev) => {
    if (ev.key === 'Escape') _closeCtxMenu();
  };
  document.addEventListener('mousedown', _ctxMenuOutsideHandler, true);
  document.addEventListener('keydown', _ctxMenuEscHandler, true);
}

function _showZoneTabContextMenu(e, panelId) {
  const def = PANELS[panelId];
  if (!def) return;
  const currentZone = _findZoneForPanel(panelId);
  const targets = def.allowedZones || ZONE_IDS.filter(z => z !== 'center' && z !== 'left');

  const zoneLabel = { left: 'Left', right: 'Right', bottom: 'Bottom', center: 'Center' };
  const items = [];
  for (const zid of targets) {
    items.push({
      label: `Dock to ${zoneLabel[zid] || zid}`,
      disabled: zid === currentZone,
      action: () => _movePanel(panelId, zid),
    });
  }
  items.push({ sep: true });
  items.push({
    label: 'Close',
    danger: true,
    action: () => hidePanel(panelId),
  });

  _openCtxMenu(items, e.clientX, e.clientY);
}

function _showZoneTabMenuDropdown(anchorBtn, zoneId, candidates) {
  const rect = anchorBtn.getBoundingClientRect();
  const items = candidates.map(def => ({
    label: `${def.icon || ''}  ${def.label}`.trim(),
    action: () => {
      // Restore + move to the zone whose "+" was clicked
      showPanel(def.id);
      if (_findZoneForPanel(def.id) !== zoneId) {
        _movePanel(def.id, zoneId);
      }
    },
  }));
  _openCtxMenu(items, rect.left, rect.bottom + 4);
}

// ---------------------------------------------------------------------------
// Drag & Drop between zones
// ---------------------------------------------------------------------------

const DRAG_MIME = 'application/x-agentcpq-panel';

/** True while a panel-tab or panel-header drag is in progress. Gates edge-strip visibility. */
let _panelDragActive = false;

function _initDropTargets() {
  for (const zoneId of ZONE_IDS) {
    const tabBar = getTabBar(zoneId);
    const content = getContentArea(zoneId);

    // No overlay — drop targets are indicated by tab bar highlight only

    // Allow drop on both tab bar and content area
    for (const el of [tabBar, content].filter(Boolean)) {
      el.addEventListener('dragover', (e) => {
        if (!e.dataTransfer.types.includes(DRAG_MIME)) return;
        e.preventDefault();
        e.stopPropagation();
        e.dataTransfer.dropEffect = 'move';
        if (tabBar) tabBar.classList.add('drop-target');
      });

      el.addEventListener('dragleave', (e) => {
        // Only remove if we're actually leaving the zone (not entering a child)
        if (!e.currentTarget.contains(e.relatedTarget)) {
          if (tabBar) tabBar.classList.remove('drop-target');
        }
      });

      el.addEventListener('drop', (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (tabBar) tabBar.classList.remove('drop-target');
        const panelId = e.dataTransfer.getData(DRAG_MIME);
        if (!_canDropInZone(panelId, zoneId)) return;
        _movePanel(panelId, zoneId);
      });
    }
  }
}

function _movePanel(panelId, targetZone) {
  // Remove from current zone
  let sourceZone = null;
  for (const zid of ZONE_IDS) {
    const panels = zoneMap[zid];
    if (!panels) continue;
    const idx = panels.indexOf(panelId);
    if (idx !== -1) {
      panels.splice(idx, 1);
      if (activePanels[zid] === panelId) {
        activePanels[zid] = panels[0] || null;
      }
      _renderZoneTabs(zid);
      _showActivePanel(zid);
      sourceZone = zid;
      break;
    }
  }

  // Auto-collapse zone when all panels are moved out (left, right, bottom only)
  if (sourceZone && sourceZone !== 'center' && zoneMap[sourceZone].length === 0) {
    collapseZone(sourceZone);
  }

  // Auto-expand target zone if it was collapsed
  expandZone(targetZone);

  // Add to target zone
  if (!zoneMap[targetZone]) zoneMap[targetZone] = [];
  zoneMap[targetZone].push(panelId);
  activePanels[targetZone] = panelId;

  // Move DOM container
  const content = getContentArea(targetZone);
  const container = _getContainer(panelId);
  if (content && container) content.appendChild(container);

  _renderZoneTabs(targetZone);
  _showActivePanel(targetZone);

  // Sync state
  state.panelZoneMap = _buildPanelZoneMap();
  state.zoneActivePanels = { ...activePanels };

  // Trigger layout persistence
  window.dispatchEvent(new CustomEvent('layout-changed'));
}

// ---------------------------------------------------------------------------
// Edge drop strips for collapsed zones
// ---------------------------------------------------------------------------

/**
 * Create persistent edge-strip elements at the edges of collapsible zones.
 * When a panel drag starts, these strips become visible so the user can
 * drop a panel back into an empty (collapsed) zone.
 */
function _createEdgeStrips() {
  const app = document.getElementById('app');
  if (!app) return;

  const strips = {
    left:   { position: 'left',   zone: 'left'   },
    right:  { position: 'right',  zone: 'right'  },
    bottom: { position: 'bottom', zone: 'bottom' },
  };

  for (const [key, cfg] of Object.entries(strips)) {
    const strip = document.createElement('div');
    strip.className = `zone-edge-strip zone-edge-${cfg.position}`;
    strip.dataset.zone = cfg.zone;
    strip.hidden = true;
    app.appendChild(strip);

    // Drop target handlers
    strip.addEventListener('dragover', (e) => {
      if (!e.dataTransfer.types.includes(DRAG_MIME)) return;
      e.preventDefault();
      e.stopPropagation();
      e.dataTransfer.dropEffect = 'move';
      strip.classList.add('active');
    });
    strip.addEventListener('dragleave', () => strip.classList.remove('active'));
    strip.addEventListener('drop', (e) => {
      e.preventDefault();
      e.stopPropagation();
      strip.classList.remove('active');
      const panelId = e.dataTransfer.getData(DRAG_MIME);
      if (!_canDropInZone(panelId, cfg.zone)) return;

      // Expand the collapsed zone and move the panel there
      expandZone(cfg.zone);
      _movePanel(panelId, cfg.zone);
    });
  }
}

/**
 * Show edge strips when a panel drag starts, hide when it ends.
 * Attached once at init time as global document listeners.
 */
function _initEdgeStripDragListeners() {
  document.addEventListener('dragstart', () => {
    // Only reveal strips if a panel-drag is in progress — file-tree / pipeline
    // rows also use HTML5 drag, and showing strips for those is misleading.
    setTimeout(() => {
      if (!_panelDragActive) return;
      for (const strip of document.querySelectorAll('.zone-edge-strip')) {
        strip.hidden = false;
      }
    }, 100);
  });

  document.addEventListener('dragend', () => {
    for (const strip of document.querySelectorAll('.zone-edge-strip')) {
      strip.hidden = true;
      strip.classList.remove('active');
    }
  });
}

// ---------------------------------------------------------------------------
// Panel visibility
// ---------------------------------------------------------------------------

function _showActivePanel(zoneId) {
  const content = getContentArea(zoneId);
  if (!content) return;

  const active = activePanels[zoneId];
  for (const child of content.children) {
    if (child.dataset && child.dataset.panel !== undefined) {
      child.hidden = (child.dataset.panel !== active);
    }
  }
}

function _activatePanel(zoneId, panelId) {
  activePanels[zoneId] = panelId;
  state.zoneActivePanels = { ...activePanels };

  _renderZoneTabs(zoneId);
  _showActivePanel(zoneId);

  // Trigger layout persistence
  window.dispatchEvent(new CustomEvent('layout-changed'));
}

// ---------------------------------------------------------------------------
// Container management
// ---------------------------------------------------------------------------

function _createPanelContainer(panelId) {
  const el = document.createElement('div');
  el.className = 'panel-container';
  el.id = `panel-${panelId}`;
  el.dataset.panel = panelId;
  panelContainers[panelId] = el;
  return el;
}

function _ensurePanelInZone(panelId, zoneId) {
  const content = getContentArea(zoneId);
  if (!content) return;

  let container = _getContainer(panelId);
  if (!container) {
    container = _createPanelContainer(panelId);
  }

  // Move to correct zone content area if not already there
  if (container.parentElement !== content) {
    content.appendChild(container);
  }
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Initialize all panels — create containers, render tabs, show defaults.
 * Call after cacheRefs() and initLayout().
 * @param {Object} [savedConfig] — layout config loaded from localStorage
 */
export function initPanels(savedConfig) {
  if (savedConfig?.panelZoneMap && typeof savedConfig.panelZoneMap === 'object') {
    // Restore panel positions from saved config.
    // savedConfig.panelZoneMap is { panelId: zoneId } — invert to zoneMap { zoneId: [panelId, ...] }.
    zoneMap = {};
    for (const zoneId of ZONE_IDS) zoneMap[zoneId] = [];
    for (const [panelId, zoneId] of Object.entries(savedConfig.panelZoneMap)) {
      if (PANELS[panelId] && ZONE_IDS.includes(zoneId)) {
        zoneMap[zoneId].push(panelId);
      }
    }
    // Restore active panels per zone, with validation
    activePanels = {};
    const savedActive = savedConfig.zoneActivePanels || {};
    for (const zoneId of ZONE_IDS) {
      const panels = zoneMap[zoneId];
      if (savedActive[zoneId] && panels.includes(savedActive[zoneId])) {
        activePanels[zoneId] = savedActive[zoneId];
      } else {
        activePanels[zoneId] = panels[0] || DEFAULT_ACTIVE[zoneId] || null;
      }
    }
    // Determine hidden panels (any panel not in any zone)
    const allAssigned = new Set(Object.values(zoneMap).flat());
    hiddenPanels.clear();
    for (const pid of Object.keys(PANELS)) {
      if (!allAssigned.has(pid)) hiddenPanels.add(pid);
    }
  } else {
    // No saved config — use defaults
    zoneMap = {};
    for (const zoneId of ZONE_IDS) {
      zoneMap[zoneId] = [...(DEFAULT_ZONE_MAP[zoneId] || [])];
    }
    activePanels = { ...DEFAULT_ACTIVE };

    // Populate hidden set
    hiddenPanels.clear();
    for (const pid of HIDDEN_BY_DEFAULT) {
      hiddenPanels.add(pid);
    }
  }

  // Create panel containers and append visible ones to their zones
  for (const zoneId of ZONE_IDS) {
    for (const panelId of zoneMap[zoneId]) {
      if (!hiddenPanels.has(panelId)) {
        _ensurePanelInZone(panelId, zoneId);
      }
    }
  }

  // Render tab bars and show active panels
  for (const zoneId of ZONE_IDS) {
    _renderZoneTabs(zoneId);
    _showActivePanel(zoneId);
  }

  // Enable drag & drop between zones
  _initDropTargets();

  // Edge drop strips for collapsed zones
  _createEdgeStrips();
  _initEdgeStripDragListeners();

  // Sync to shared state
  state.panelZoneMap = _buildPanelZoneMap();
  state.zoneActivePanels = { ...activePanels };
}

/**
 * Show a panel in its zone. If hidden, un-hides and activates it.
 * If already visible but not active, activates it.
 */
export function showPanel(panelId) {
  const def = PANELS[panelId];
  if (!def) return;

  hiddenPanels.delete(panelId);

  // Find which zone it's currently in
  let currentZone = _findZoneForPanel(panelId);

  if (!currentZone) {
    // Not in any zone — add to default zone
    currentZone = def.defaultZone;
    if (!zoneMap[currentZone]) zoneMap[currentZone] = [];
    if (!zoneMap[currentZone].includes(panelId)) {
      zoneMap[currentZone].push(panelId);
    }
    _ensurePanelInZone(panelId, currentZone);
  }

  // Sync state BEFORE _activatePanel (which triggers layout save via layout-changed event)
  state.panelZoneMap = _buildPanelZoneMap();

  // Activate it
  _activatePanel(currentZone, panelId);
}

/**
 * Hide a panel from its zone.
 */
export function hidePanel(panelId) {
  const def = PANELS[panelId];
  if (!def) return;

  hiddenPanels.add(panelId);

  const zoneId = _findZoneForPanel(panelId);
  if (!zoneId) return;

  // Remove from zone
  zoneMap[zoneId] = zoneMap[zoneId].filter(id => id !== panelId);

  // Remove DOM container from zone content
  const container = _getContainer(panelId);
  if (container && container.parentElement) {
    container.parentElement.removeChild(container);
  }

  // If the hidden panel was active, switch to another
  if (activePanels[zoneId] === panelId) {
    activePanels[zoneId] = zoneMap[zoneId][0] || null;
  }

  _renderZoneTabs(zoneId);
  _showActivePanel(zoneId);

  // Auto-collapse zone when all panels are hidden (left, right, bottom only)
  if (zoneId !== 'center' && zoneMap[zoneId].length === 0) {
    collapseZone(zoneId);
  }

  // Sync state
  state.panelZoneMap = _buildPanelZoneMap();
  state.zoneActivePanels = { ...activePanels };

  // Trigger layout persistence
  window.dispatchEvent(new CustomEvent('layout-changed'));
}

/**
 * Toggle a panel:
 *  - If hidden -> show
 *  - If visible and active -> hide
 *  - If visible but not active -> activate
 */
export function togglePanel(panelId) {
  if (hiddenPanels.has(panelId)) {
    showPanel(panelId);
    return;
  }

  const zoneId = _findZoneForPanel(panelId);
  if (!zoneId) {
    // Not in any zone (shouldn't happen if not hidden, but be safe)
    showPanel(panelId);
    return;
  }

  if (activePanels[zoneId] === panelId) {
    // Active in its zone — but if the zone is collapsed, the panel isn't
    // actually visible. Expand the zone instead of hiding the panel.
    if (_isZoneCollapsed(zoneId)) {
      expandZone(zoneId);
      window.dispatchEvent(new CustomEvent('layout-changed'));
    } else {
      hidePanel(panelId);
    }
  } else {
    // Visible but not active — activate (and expand zone if collapsed)
    if (_isZoneCollapsed(zoneId)) {
      expandZone(zoneId);
    }
    _activatePanel(zoneId, panelId);
  }
}

/**
 * Get the DOM container element for a panel.
 * Creates it if it doesn't exist yet.
 */
export function getPanelContainer(panelId) {
  let el = _getContainer(panelId);
  if (!el) {
    el = _createPanelContainer(panelId);
  }
  return el;
}

/**
 * Get the active panel ID for a zone.
 */
export function getActivePanel(zoneId) {
  return activePanels[zoneId] || null;
}

/**
 * Get which zone a panel is currently in.
 */
export function getZoneForPanel(panelId) {
  return _findZoneForPanel(panelId);
}

/**
 * Make the header element inside a panel draggable for drag-and-drop between zones.
 * Call after populating the panel's innerHTML. The header is found by looking for
 * common header class names (.console-header, .chat-panel-header, etc.).
 */
export function makePanelHeaderDraggable(panelId) {
  const def = PANELS[panelId];
  if (!def || def.pinned) return;

  const container = _getContainer(panelId);
  if (!container) return;

  const header = container.querySelector(
    '.console-header, .quote-frame-header, .chat-panel-header, ' +
    '.explorer-header, .pipeline-header, .kb-header, .db-inspector-header, .diff-header, .sync-status-header, .tl-head, .traces-head, .prom-head, .errors-head, .learning-head'
  );
  if (!header) return;

  header.draggable = true;
  header.style.cursor = 'grab';
  header.addEventListener('dragstart', (e) => {
    e.dataTransfer.setData(DRAG_MIME, panelId);
    e.dataTransfer.effectAllowed = 'move';
    container.style.opacity = '0.4';
    header.style.cursor = 'grabbing';
    _panelDragActive = true;
  });
  header.addEventListener('dragend', () => {
    container.style.opacity = '';
    header.style.cursor = 'grab';
    _panelDragActive = false;
  });
}

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

function _findZoneForPanel(panelId) {
  for (const zoneId of ZONE_IDS) {
    if (zoneMap[zoneId] && zoneMap[zoneId].includes(panelId)) {
      return zoneId;
    }
  }
  return null;
}

function _buildPanelZoneMap() {
  const map = {};
  for (const zoneId of ZONE_IDS) {
    for (const panelId of (zoneMap[zoneId] || [])) {
      map[panelId] = zoneId;
    }
  }
  return map;
}
